import { useState, useEffect, useMemo } from 'react';

export const useUserReports = (userId) => {
    const [reports, setReports] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [timeFilter, setTimeFilter] = useState('all');
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 5,
        total: 0,
        totalPages: 0
    });
    const [selectedPet, setSelectedPet] = useState(null); 


    const groupReportsByPet = (reports) => {
        return reports.reduce((acc, report) => {
            const petName = report.petName || 'Sin nombre';
            if (!acc[petName]) {
                acc[petName] = [];
            }
            acc[petName].push(report);
            return acc;
        }, {});
    };

    const groupedByPet = useMemo(() => {
        const grouped = groupReportsByPet(reports);
        return grouped;
    }, [reports]);


    // Group reports by time periods using processing_start_time
    const groupedReports = useMemo(() => {
        if (!reports.length) return {};

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const lastWeek = new Date(today);
        lastWeek.setDate(lastWeek.getDate() - 7);

        const lastMonth = new Date(today);
        lastMonth.setMonth(lastMonth.getMonth() - 1);

        return reports.reduce((acc, report) => {
            const reportDate = new Date(report.created_at);

            if (isNaN(reportDate.getTime())) {
                console.error('Invalid date:', report.created_at);
                return acc;
            }

            if (reportDate >= today) {
                acc.today = [...(acc.today || []), report];
            } else if (reportDate >= lastWeek) {
                acc.week = [...(acc.week || []), report];
            } else if (reportDate >= lastMonth) {
                acc.month = [...(acc.month || []), report];
            } else {
                acc.older = [...(acc.older || []), report];
            }

            return acc;
        }, { today: [], week: [], month: [], older: [] });
    }, [reports]);

    // Get filtered reports based on time period
    const filteredReports = useMemo(() => {
        if (timeFilter === 'all') return reports;
        return groupedReports[timeFilter] || [];
    }, [reports, timeFilter, groupedReports]);

    // Apply pagination to filtered reports
    const paginatedReports = useMemo(() => {
        const startIndex = (pagination.page - 1) * pagination.limit;
        return filteredReports.slice(startIndex, startIndex + pagination.limit);
    }, [filteredReports, pagination.page, pagination.limit]);

    // Get counts for tab badges
    const reportCounts = useMemo(() => ({
        all: reports.length,
        today: groupedReports.today?.length || 0,
        week: groupedReports.week?.length || 0,
        month: groupedReports.month?.length || 0,
        older: groupedReports.older?.length || 0
    }), [reports, groupedReports]);



    const fetchReports = async (page = 1) => {
        if (!userId) return;

        try {
            setLoading(true);
            setError(null);

            const response = await fetch(`/api/analysis/${userId}`);
            if (!response.ok) {
                const result = await response.json();
                throw new Error(result.error || 'Error fetching analyses');
            }

            const result = await response.json();

            if (result.success) {
                const transformedReports = result.data.map(item => ({
                    id: item.requestId,
                    created_at: item.timestamp,
                    context: item.userContext,
                    analysis: item.analysis,
                    images: item.images,
                    breed: item.breed,
                    analysisType: item.analysisType,
                    petName: item.petName
                }));

                setReports(transformedReports);

                // Update pagination based on filtered results
                const filteredTotal = timeFilter === 'all'
                    ? transformedReports.length
                    : (groupedReports[timeFilter]?.length || 0);

                const totalPages = Math.ceil(filteredTotal / pagination.limit);

                setPagination(prev => ({
                    ...prev,
                    total: filteredTotal,
                    totalPages,
                    page: Math.min(page, totalPages || 1)
                }));
            }
        } catch (err) {
            console.error('Error fetching analyses:', err);
            setError(err.message);
            setReports([]);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({
                ...prev,
                page: newPage
            }));
        }
    };

    // Reset pagination when time filter changes
    useEffect(() => {
        setPagination(prev => ({
            ...prev,
            page: 1
        }));
    }, [timeFilter]);

    useEffect(() => {
        if (userId) {
            fetchReports(1);
        }
    }, [userId]);

    return {
        reports: paginatedReports,
        allReports: reports,
        loading,
        error,
        pagination,
        timeFilter,
        setTimeFilter,
        reportCounts,
        fetchReports,
        handlePageChange,
        groupedReports,
        groupedByPet,
        selectedPet,
        setSelectedPet
    };
};

export default useUserReports;